/**
 * Data processor: flatten Elasticsearch hits into a usable `articleIds` array.
 *
 * The elasticsearch_endpoint passthrough returns ES's native shape:
 *
 *   { hits: { hits: [{ _source: { nid: [42], object_slug: ["foo"] }, ...}], ... } }
 *
 * Every scalar field in `_source` is wrapped in an array (ES convention — even
 * mono-valued fields). Reactive-json's `simpleMapping` extracts dot-paths from
 * the response but cannot project an array-of-objects into a flat array (no
 * JSONPath-like `[]` operator).
 *
 * This processor enriches the response in place with two derived keys:
 *
 *   - `articleIds`   : flat array of nids (e.g. [42, 43, 51])
 *   - `articleSlugs` : flat array of slugs in the same order
 *
 * which the parent rjBuild can then dispatch via `simpleMapping` and pass to a
 * `ReactiveJsonSubroot` for the second fetch (decoupled_toolbox collection by
 * IDs).
 *
 * The processor only fires on `/api/elasticsearch/` URLs to avoid polluting
 * every fetch — it's a builder-specific aid, not a generic ES utility.
 */
export const flattenEsHits = ({ requestContext, dataToProcess }) => {
  if (!requestContext?.url?.includes('/api/elasticsearch/')) {
    return dataToProcess
  }
  const hits = dataToProcess?.hits?.hits
  if (!Array.isArray(hits)) {
    return dataToProcess
  }

  const articleIds = hits
    .map((h) => h?._source?.nid?.[0])
    .filter((id) => id != null)

  const articleSlugs = hits
    .map((h) => h?._source?.object_slug?.[0])
    .filter((s) => s != null)

  return { ...dataToProcess, articleIds, articleSlugs }
}
